/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// Document and DB Subscriptions (Sync) Service
/// Tracking and Utilizing Subscribed Data States
/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const useDocStore = defineStore('docStore', () => {
  // //////////////////////////////////////////////////////////////////
  // Holds Synced Subscription Statuses
  // //////////////////////////////////////////////////////////////////
  const docs = ref<any>({})

  // //////////////////////////////////////////////////////////////////
  // 💬 Get Subscription to a Collection
  // //////////////////////////////////////////////////////////////////
  // Specify a keyName So Syncs can be tracked and unsubscribed
  // isSync = true if keeping document in sync with firestore server
  // Specify a syncId to only modify documents that have been changed

  const getDoc = async (keyName: string, docPath: string, docId: string, isSync: boolean = false, storeReference: any = null) => {
    // If keyName Doesn't Exist or Doesn't Have a "data" key then create it
    const docKeys = { data: {}, started_at: new Date(), isSync }
    if (!docs.value[keyName])
      docs.value[keyName] = docKeys

    // If Missing Default Properties
    docs.value[keyName] = { ...docKeys, ...docs.value[keyName] }

    // Get Data From Subscription Service.
    // The callback is defensive: if the entry was wiped between when we
    // subscribed and when this callback fires (e.g. user signed out and
    // userLogout cleared all docs), `docs.value[keyName]` is undefined
    // and `.data = data` throws. Guard with an existence check so a
    // late-arriving snapshot is silently dropped instead of crashing.
    const unsub = useDoc(docPath, docId, isSync, data => {
      if (docs.value[keyName]) docs.value[keyName].data = data
      if (storeReference) storeReference.value = data
    })

    // Write Out the "unsub" callable function key for unsubscribing
    if (isSync) {
      if (docs.value[keyName]) {
        if (!docs.value[keyName].unsub)
          docs.value[keyName].unsub = await unsub
      }
      else {
        docs.value[keyName] = { unsub: await unsub, started_at: new Date(), isSync }
        console.log(`Created New Non-Sync for ${keyName}`, docs.value[keyName])
      }
    }
  }

  const getCollection = async (keyName: string, collectionPath: string, whereClauses: any[] = [], isSync: boolean = false, syncId: string | null = null, storeReference: any = null) => {
    // If keyName Doesn't Exist or Doesn't Have a "data" key then create it
    const docKeys = { data: [], started_at: new Date(), isSync }
    if (!docs.value[keyName])
      docs.value[keyName] = docKeys

    // If Missing Default Properties
    docs.value[keyName] = { ...docKeys, ...docs.value[keyName] }

    // Get Data From Subscription Service.
    // Same race-safety as `getDoc`: bail if the entry was wiped (e.g.
    // by userLogout) between subscribe + callback.
    const unsub = useCollection(collectionPath, whereClauses, isSync, (data, changes) => {
      if (!docs.value[keyName]) return

      // If Not Incremental Changes
      if (!syncId || !docs.value[keyName].data || docs.value[keyName].data.length === 0) {
        docs.value[keyName].data = data
      }

      // If Incremental Changes Using Added, Modified, and Removed
      // Helps preventing full single page reloads
      else {
        changes.forEach((change: any) => {
          const changeDoc = change.doc

          // If Modifying Document to Existing Document
          if (change.type === 'modified') {
            // Get Existing Document Index
            const docIndex = docs.value[keyName].data.findIndex((doc: any) => doc[syncId] === changeDoc[syncId])

            // Replace Existing Document with Modified Document
            if (docIndex !== -1)
              docs.value[keyName].data[docIndex] = changeDoc
            else docs.value[keyName].data.push(changeDoc)
          }

          // If Adding a New Document to Collection
          if (change.type === 'added')
            docs.value[keyName].data.push(changeDoc)

          // If Removing a Document from Collection
          if (change.type === 'removed') {
            const docIndex = docs.value[keyName].data.findIndex((doc: any) => doc[syncId] === changeDoc[syncId])
            if (docIndex !== -1)
              docs.value[keyName].data.splice(docIndex, 1)
          }
        })
      }
    })

    // Write Out the "unsub" callable function key for unsubscribing
    if (isSync) {
      if (docs.value[keyName]) {
        if (!docs.value[keyName].unsub)
          docs.value[keyName].unsub = await unsub
      }
      else {
        docs.value[keyName] = { unsub: await unsub, started_at: new Date(), isSync }
      }
    }
  }

  // Get Data from an Already Called Document
  function get(keyName: string, defaultReturnValue: [] | {} | '' = []) {
    // If Ref Object Doesn't Already Exists Then Create It So It Can Be Subscribed To
    if (!docs.value[keyName])
      docs.value[keyName] = { data: defaultReturnValue }

    if (!docs.value[keyName].data)
      docs.value[keyName].data = defaultReturnValue

    return computed(() => docs.value[keyName].data)
  }

  // //////////////////////////////////////////////////////////////////
  // 💬  Unsubscribe from a Subscribed Collection or Document
  // //////////////////////////////////////////////////////////////////
  // Automatically Drops from Docs Unless "drop" is set to false
  function unsub(keyName: string, drop: boolean = true) {
    if (!docs.value[keyName])
      return 'No Subscription Found'

    if (!docs.value[keyName].unsub) {
      if (drop)
        delete docs.value[keyName]

      return 'Not Currently Subscribed'
    }
    else {
      docs.value[keyName].unsub()
      if (drop)
        delete docs.value[keyName]
      else docs.value[keyName].isSync = false

      return 'Unsubscribed'
    }
  }

  return { docs, get, getDoc, getCollection, unsub }
})
